import type { AuthenticatedUser, LoginRequest } from "@brainics/api-contract";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  ApiClientError,
  getCurrentSession,
  login as createSession,
  logout as destroySession,
} from "../lib/api.js";

export type AuthenticationStatus = "checking" | "anonymous" | "authenticated";

interface AuthState {
  status: AuthenticationStatus;
  user: AuthenticatedUser | null;
  expiresAt: string | null;
  initializationError: boolean;
}

interface AuthContextValue extends AuthState {
  signIn(input: LoginRequest): Promise<void>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const anonymousState: AuthState = {
  status: "anonymous",
  user: null,
  expiresAt: null,
  initializationError: false,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: "checking",
    user: null,
    expiresAt: null,
    initializationError: false,
  });

  const refresh = useCallback(async () => {
    try {
      const result = await getCurrentSession();
      setState({
        status: "authenticated",
        user: result.user,
        expiresAt: result.session.expiresAt,
        initializationError: false,
      });
    } catch (error) {
      const expectedAnonymous = error instanceof ApiClientError && error.status === 401;
      setState({
        ...anonymousState,
        initializationError: !expectedAnonymous,
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async (input: LoginRequest) => {
    const result = await createSession(input);
    setState({
      status: "authenticated",
      user: result.user,
      expiresAt: result.session.expiresAt,
      initializationError: false,
    });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await destroySession();
    } catch (error) {
      if (!(error instanceof ApiClientError) || error.status !== 401) throw error;
    }
    setState(anonymousState);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut, refresh }),
    [refresh, signIn, signOut, state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
